import { authStorageService } from "@/features/auth/services/storage.service";
import { getApiBaseUrl } from "@/shared/utils/api.util";

export const USAGE_TRACKING_ENDPOINTS = {
  VISITS: "/usage_tracking/visits",
  EVENTS: "/usage_tracking/events",
} as const;

const APP_NAME = "benef";
const PLATFORM = "web";
const PAGE_VIEW_EVENT = "$view";
const FLUSH_INTERVAL_MS = 10_000;
const FLUSH_THRESHOLD = 20;
const MAX_EVENTS_PER_BATCH = 50;
const MAX_PAGE_LENGTH = 200;
const VISIT_INACTIVITY_MS = 30 * 60 * 1000;
// Shared by every tab of the browser: they all report the same visit.
export const VISIT_STORAGE_KEY = "yeati_usage_visit";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type SharedVisit = { token: string, lastEventAt: number };

type UsageEvent = {
  id: string
  name: typeof PAGE_VIEW_EVENT
  properties: { page: string }
  time: string
};

type EnabledResponse = { enabled?: unknown };

export type UsageTrackerDependencies = {
  getAccessToken: () => string | null
  getVisitorToken: () => string | null
  resolveUrl: (path: string) => string
  appVersion?: string
};

export type UsageTracker = {
  start: () => void
  stop: () => void
  trackView: (page: string) => void
};

// Returns the server switch value, or null when the call failed (errors are swallowed on purpose).
const postJson = async (
  url: string,
  accessToken: string,
  body: unknown,
  keepalive: boolean,
): Promise<boolean | null> => {
  try {
    const response = await fetch(url, {
      method: "POST",
      keepalive,
      headers: {
        "Content-Type": "application/json",
        "X-Client-Type": PLATFORM,
        "Authorization": `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) return null;
    const data = (await response.json()) as EnabledResponse;
    return typeof data.enabled === "boolean" ? data.enabled : null;
  }
  catch {
    return null;
  }
};

// Storage errors and invalid values are ignored: the tab then keeps its visit in memory.
const readSharedVisit = (): SharedVisit | null => {
  try {
    const raw = window.localStorage.getItem(VISIT_STORAGE_KEY);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object" || !("token" in value) || !("lastEventAt" in value)) return null;
    const { token, lastEventAt } = value;
    if (typeof token !== "string" || !UUID_PATTERN.test(token)) return null;
    if (typeof lastEventAt !== "number" || !Number.isFinite(lastEventAt)) return null;
    return { token, lastEventAt };
  }
  catch {
    return null;
  }
};

const writeSharedVisit = (visit: SharedVisit) => {
  try {
    window.localStorage.setItem(VISIT_STORAGE_KEY, JSON.stringify(visit));
  }
  catch {
    // Storage full or unavailable.
  }
};

const clearSharedVisit = () => {
  try {
    window.localStorage.removeItem(VISIT_STORAGE_KEY);
  }
  catch {
    // Storage unavailable.
  }
};

export const createUsageTracker = ({
  getAccessToken, getVisitorToken, resolveUrl, appVersion,
}: UsageTrackerDependencies): UsageTracker => {
  let isDisabledUntilReload = false;
  let isRunning = false;
  let visitToken = "";
  let visitRequest: Promise<boolean> | null = null;
  let queue: UsageEvent[] = [];
  let lastPage: string | null = null;
  let lastEventAt: number | null = null;
  let flushTimer: ReturnType<typeof setInterval> | undefined;

  const disable = () => {
    isDisabledUntilReload = true;
    queue = [];
  };

  const handleSwitch = (enabled: boolean | null): boolean => {
    if (enabled === false) disable();
    return enabled === true;
  };

  const requestVisit = (accessToken: string, visitorToken: string): Promise<boolean> => {
    const request = postJson(resolveUrl(USAGE_TRACKING_ENDPOINTS.VISITS), accessToken, {
      visit_token: visitToken,
      visitor_token: visitorToken,
      app: APP_NAME,
      platform: PLATFORM,
      ...(appVersion ? { app_version: appVersion } : {}),
    }, false).then(handleSwitch);
    visitRequest = request;
    // A failed visit is attempted again at the next flush, never in a loop.
    void request.then((isConfirmed) => {
      if (!isConfirmed && visitRequest === request) visitRequest = null;
    });
    return request;
  };

  // Never written once logged out, so the next login does not resume the previous user's visit.
  const saveSharedVisit = (eventAt: number) => {
    if (getAccessToken()) writeSharedVisit({ token: visitToken, lastEventAt: eventAt });
  };

  // Resumes the visit of another tab when it is still running, otherwise starts a new one.
  const startVisit = () => {
    const now = Date.now();
    const shared = readSharedVisit();
    if (shared && now - shared.lastEventAt <= VISIT_INACTIVITY_MS) {
      visitToken = shared.token;
      lastEventAt = shared.lastEventAt;
    }
    else {
      visitToken = crypto.randomUUID();
      saveSharedVisit(now);
    }
    visitRequest = null;
    const accessToken = getAccessToken();
    const visitorToken = getVisitorToken();
    if (accessToken && visitorToken) void requestVisit(accessToken, visitorToken);
  };

  const flush = (keepalive = false) => {
    if (isDisabledUntilReload || queue.length === 0) return;
    const accessToken = getAccessToken();
    const visitorToken = getVisitorToken();
    const events = queue;
    queue = [];
    if (!accessToken || !visitorToken) return;

    const batchVisitToken = visitToken;
    const visitConfirmation = visitRequest ?? requestVisit(accessToken, visitorToken);
    void visitConfirmation.then(async (isConfirmed) => {
      if (!isConfirmed) return;
      for (let index = 0; index < events.length; index += MAX_EVENTS_PER_BATCH) {
        if (isDisabledUntilReload) return;
        const enabled = await postJson(resolveUrl(USAGE_TRACKING_ENDPOINTS.EVENTS), accessToken, {
          visit_token: batchVisitToken,
          visitor_token: visitorToken,
          events: events.slice(index, index + MAX_EVENTS_PER_BATCH),
        }, keepalive);
        handleSwitch(enabled);
      }
    });
  };

  // Another tab may have extended the visit since this tab's last view.
  const isVisitExpired = (now: number) => {
    const shared = readSharedVisit();
    const sharedEventAt = shared?.token === visitToken ? shared.lastEventAt : null;
    const lastActivityAt = Math.max(lastEventAt ?? -Infinity, sharedEventAt ?? -Infinity);
    return Number.isFinite(lastActivityAt) && now - lastActivityAt > VISIT_INACTIVITY_MS;
  };

  const enqueueView = (page: string, now: number) => {
    queue.push({
      id: crypto.randomUUID(),
      name: PAGE_VIEW_EVENT,
      properties: { page },
      time: new Date(now).toISOString(),
    });
    lastPage = page;
    lastEventAt = now;
    saveSharedVisit(now);
    if (queue.length >= FLUSH_THRESHOLD) flush();
  };

  const rotateVisit = () => {
    flush();
    startVisit();
  };

  const trackView = (rawPage: string) => {
    if (!isRunning || isDisabledUntilReload) return;
    const page = rawPage.slice(0, MAX_PAGE_LENGTH);
    if (page === lastPage) return;
    const now = Date.now();
    if (isVisitExpired(now)) rotateVisit();
    enqueueView(page, now);
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      flush(true);
      return;
    }
    const now = Date.now();
    if (isDisabledUntilReload || !isVisitExpired(now)) return;
    rotateVisit();
    if (lastPage) enqueueView(lastPage, now);
  };

  const handlePageHide = () => flush(true);

  const start = () => {
    if (isRunning || isDisabledUntilReload) return;
    isRunning = true;
    startVisit();
    flushTimer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
  };

  const stop = () => {
    if (!isRunning) return;
    flush();
    isRunning = false;
    clearInterval(flushTimer);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pagehide", handlePageHide);
    queue = [];
    visitRequest = null;
    lastPage = null;
    lastEventAt = null;
    clearSharedVisit();
  };

  return { start, stop, trackView };
};

export const usageTracker = createUsageTracker({
  getAccessToken: () => authStorageService.getAuthToken(),
  getVisitorToken: () => authStorageService.findAuthDeviceId(),
  resolveUrl: getApiBaseUrl,
  appVersion: import.meta.env.VITE_APP_VERSION,
});
